import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { 
  Plus, 
  Trash2, 
  Save, 
  ArrowLeft,
  Package,
  DollarSign,
  Calendar,
  FileText,
  Search,
  ChevronDown,
  X
} from 'lucide-react';
import { purchaseReturnsAPI, itemsAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import dayjs from '../../utils/dayjs';
import TotalsBar from '../ui/TotalsBar';

const PurchaseReturnForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();
  const isEditing = Boolean(id);

  // Form setup
  const { register, control, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm({
    defaultValues: {
      PurchaseReturnDate: dayjs().format('YYYY-MM-DD'),
      Remarks: '',
      details: []
    }
  });

  const { fields, append, prepend, remove, move } = useFieldArray({
    control,
    name: 'details'
  });

  // State for items and purchase return data
  const [items, setItems] = useState([]);
  const [purchaseReturnData, setPurchaseReturnData] = useState(null);
  const [isLoadingPurchaseReturn, setIsLoadingPurchaseReturn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Barcode scanning state
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [isSearchingByBarcode, setIsSearchingByBarcode] = useState(false);
  const [barcodeQueue, setBarcodeQueue] = useState([]);
  const [isProcessingBarcodeQueue, setIsProcessingBarcodeQueue] = useState(false);
  const barcodeInputRef = useRef(null);
  const quantityInputRefs = useRef({});
  // Track previous processing state to run a callback after processing completes
  const wasProcessingBarcodeRef = useRef(false);

  // Barcode/search input dropdown state
  const [barcodeSearchResults, setBarcodeSearchResults] = useState([]);
  const [isBarcodeSearching, setIsBarcodeSearching] = useState(false);
  const [showBarcodeDropdown, setShowBarcodeDropdown] = useState(false);

  // Searchable dropdown state
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [isSearching, setIsSearching] = useState({});

  // Fetch items for dropdown
  const fetchItems = async () => {
    try {
      const response = await itemsAPI.getAll({ limit: 1000, include_out_of_stock: true });
      setItems(response.data || []);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load items'
      });
    }
  };

  // Fetch purchase return data if editing
  const fetchPurchaseReturn = async () => {
    if (!isEditing) return;
    
    try {
      setIsLoadingPurchaseReturn(true);
      const response = await purchaseReturnsAPI.get(id);
      setPurchaseReturnData(response.data);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load purchase return data'
      });
    } finally {
      setIsLoadingPurchaseReturn(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchItems();
    if (isEditing) {
      fetchPurchaseReturn();
    }
  }, [isEditing, id]);

  // Focus barcode input on component mount
  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  // Callback-style focus after barcode processing toggles from true -> false
  useEffect(() => {
    if (wasProcessingBarcodeRef.current && !isProcessingBarcodeQueue) {
      // Processing just finished; ensure barcode gets focus now that UI settled
      focusBarcodeInput();
    }
    wasProcessingBarcodeRef.current = isProcessingBarcodeQueue;
  }, [isProcessingBarcodeQueue]);

  // API-based search function
  const searchItems = useCallback(async (index, searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults(prev => ({
        ...prev,
        [index]: []
      }));
      return;
    }

    setIsSearching(prev => ({
      ...prev,
      [index]: true
    }));

    try {
      const response = await itemsAPI.search(searchTerm, { include_out_of_stock: true });
      setSearchResults(prev => ({
        ...prev,
        [index]: response.data || []
      }));
    } catch (error) {
      console.error('Error searching items:', error);
      setSearchResults(prev => ({
        ...prev,
        [index]: []
      }));
    } finally {
      setIsSearching(prev => ({
        ...prev,
        [index]: false
      }));
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setDropdownStates({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounced search effect for each dropdown
  useEffect(() => {
    const timeouts = {};
    
    Object.keys(searchTerms).forEach(index => {
      const searchTerm = searchTerms[index];
      if (searchTerm !== undefined) {
        // Clear existing timeout
        if (timeouts[index]) {
          clearTimeout(timeouts[index]);
        }
        
        // Set new timeout for debounced search
        timeouts[index] = setTimeout(() => {
          searchItems(parseInt(index), searchTerm);
        }, 300);
      }
    });

    // Cleanup function
    return () => {
      Object.values(timeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [searchTerms, searchItems]);

  // Set form data when purchase return is loaded
  useEffect(() => {
    if (purchaseReturnData && isEditing) {
      setValue('PurchaseReturnDate', purchaseReturnData.PurchaseReturnDate ? dayjs(purchaseReturnData.PurchaseReturnDate).format('YYYY-MM-DD') : '');
      setValue('Remarks', purchaseReturnData.Remarks || '');
      setValue('details', purchaseReturnData.details || []);
    }
  }, [purchaseReturnData, isEditing, setValue]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      
      // Convert date to UTC ISO string
      const formattedData = {
        ...data,
        PurchaseReturnDate: dayjs(data.PurchaseReturnDate).utc().toISOString(),
        details: data.details.map(detail => ({
          ...detail,
          Quantity: parseInt(detail.Quantity) || 0,
          Price: parseFloat(detail.Price) || 0,
          SaleItem_ID: detail.SaleItem_ID ? parseInt(detail.SaleItem_ID) : null,
        }))
      };

      await purchaseReturnsAPI.create(formattedData);
      
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Purchase return created successfully'
      });
      
      navigate('/purchase-returns');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to create purchase return'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Long returns scroll, so say which rows failed rather than leaving the red
  // messages to be found.
  const onInvalid = (formErrors) => {
    const overStock = (formErrors.details || [])
      .map((rowErrors, index) => (rowErrors?.Quantity?.type === 'validate' ? index : null))
      .filter(index => index !== null)
      .map(index => {
        const itemId = Number(getValues(`details.${index}.SaleItem_ID`));
        return items.find(i => i && i.ID === itemId)?.Name || `Row ${index + 1}`;
      });
    if (overStock.length === 0) return;
    addNotification({
      type: 'error',
      title: 'More than in stock',
      message: `Lower the quantity for: ${overStock.join(', ')}`
    });
  };

  const addDetail = () => {
    prepend({ Quantity: '', Price: '', SaleItem_ID: null, Remarks: '' });
    // Open the item dropdown for the newly added top row and focus its search input
    setDropdownStates(prev => ({
      ...prev,
      0: true
    }));
    // Defer focusing to after the dropdown renders
    setTimeout(() => {
      const searchInput = document.querySelector('.dropdown-container input[placeholder="Search items..."]');
      if (searchInput) {
        searchInput.focus();
        searchInput.select();
      }
    }, 0);
  };

  const removeDetail = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Searchable dropdown helper functions
  const toggleDropdown = (index) => {
    setDropdownStates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const setSearchTerm = (index, term) => {
    setSearchTerms(prev => ({
      ...prev,
      [index]: term
    }));
  };

  const selectItem = (index, item) => {
    if (rejectIfNoStock(item)) return;

    // Keep the picked item in `items` first. The row renders its label by looking
    // the stored id up there, and searchResults[index] — where this item came
    // from — is cleared a few lines below. Only ~1000 items are preloaded out of
    // tens of thousands, so without this the row goes back to "Select an item"
    // the moment anything outside that first page is chosen. Mirrors what
    // addItemFromSearch and the barcode scanner already do.
    setItems(prev => {
      const previous = Array.isArray(prev) ? prev : [];
      if (previous.some(i => i && i.ID === item.ID)) return previous;
      return [item, ...previous];
    });

    setValue(`details.${index}.SaleItem_ID`, item.ID);
    setValue(`details.${index}.Price`, item.Price || 0);
    setValue(`details.${index}.Quantity`, '1');
    setDropdownStates(prev => ({
      ...prev,
      [index]: false
    }));
    setSearchTerms(prev => ({
      ...prev,
      [index]: ''
    }));
    setSearchResults(prev => ({
      ...prev,
      [index]: []
    }));
  };

  const getFilteredItems = (index) => {
    const searchTerm = searchTerms[index] || '';
    if (!searchTerm.trim()) {
      return items; // Show all items when no search term
    }
    return searchResults[index] || [];
  };

  const getSelectedItem = (index) => {
    const selectedId = watch(`details.${index}.SaleItem_ID`);
    // First try to find in search results, then fall back to all items
    const searchResultsForIndex = searchResults[index] || [];
    const foundInSearch = searchResultsForIndex.find(item => item.ID === selectedId);
    if (foundInSearch) return foundInSearch;
    
    return items.find(item => item.ID === selectedId);
  };

  // A return can't take out more than the system holds: the server rejects the
  // whole return when any line exceeds stock, naming only the first offender, so
  // staff ended up deleting zero-stock lines one failed save at a time. Check as
  // items go in instead. A zero-stock hit is usually a mistyped barcode.
  const availableStock = (item) => Number(item?.Stock) || 0;

  const rejectIfNoStock = useCallback((item) => {
    if (availableStock(item) > 0) return false;
    addNotification({
      type: 'error',
      title: 'Not added: 0 in stock',
      message: `${item.Name}${item.Barcode ? ` (barcode ${item.Barcode})` : ''} has 0 in stock, so it can't be returned. Check the code you scanned or typed.`
    });
    return true;
  }, [addNotification]);

  const canAddOneMore = useCallback((item, currentQuantity) => {
    const stock = availableStock(item);
    if (currentQuantity + 1 <= stock) return true;
    addNotification({
      type: 'error',
      title: 'Stock limit reached',
      message: `Only ${stock} of ${item.Name} in stock; it's already on this return ${currentQuantity} time${currentQuantity === 1 ? '' : 's'}.`
    });
    return false;
  }, [addNotification]);

  // Search-as-you-type for the barcode input, so a partial name or barcode
  // offers matches instead of requiring an exact scan.
  useEffect(() => {
    const trimmed = barcodeSearch.trim();
    if (!trimmed || trimmed.length < 2) {
      setBarcodeSearchResults([]);
      setShowBarcodeDropdown(false);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsBarcodeSearching(true);
      try {
        const response = await itemsAPI.search(trimmed, { include_out_of_stock: true });
        setBarcodeSearchResults(response.data || []);
        setShowBarcodeDropdown(true);
      } catch (error) {
        console.error('Error searching items:', error);
        setBarcodeSearchResults([]);
      } finally {
        setIsBarcodeSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [barcodeSearch]);

  // Add an item picked from the search dropdown
  const addItemFromSearch = useCallback((item) => {
    if (rejectIfNoStock(item)) {
      setBarcodeSearch('');
      setBarcodeSearchResults([]);
      setShowBarcodeDropdown(false);
      focusBarcodeInput();
      return;
    }

    // Keep the item available to the per-row dropdowns
    setItems(prev => {
      const previous = Array.isArray(prev) ? prev : [];
      if (previous.some(i => i && i.ID === item.ID)) return previous;
      return [item, ...previous];
    });

    // Already on the return? Increment rather than adding a duplicate row —
    // same behaviour as the scan queue.
    const currentDetails = getValues('details') || [];
    const existingIndex = currentDetails.findIndex(d => d && d.SaleItem_ID === item.ID);
    if (existingIndex !== -1) {
      const currentQuantity = parseInt(currentDetails[existingIndex].Quantity) || 0;
      if (canAddOneMore(item, currentQuantity)) {
        setValue(`details.${existingIndex}.Quantity`, String(currentQuantity + 1));
        try {
          if (existingIndex > 0) move(existingIndex, 0);
        } catch { /* ignore reordering errors */ }
      }
    } else {
      prepend({ Quantity: '1', Price: item.Price || 0, SaleItem_ID: item.ID, Remarks: '' });
    }

    setBarcodeSearch('');
    setBarcodeSearchResults([]);
    setShowBarcodeDropdown(false);
    focusBarcodeInput();
  }, [getValues, setValue, move, prepend, rejectIfNoStock, canAddOneMore]);

  // Barcode scanning functions
  const handleBarcodeSearch = useCallback(async () => {
    const trimmed = String(barcodeSearch || '').trim();
    if (!trimmed) return;

    // If the search turned up matches, take the first one
    if (barcodeSearchResults.length > 0) {
      addItemFromSearch(barcodeSearchResults[0]);
      return;
    }

    // Otherwise fall back to an exact barcode lookup via the queue worker
    setBarcodeQueue(prev => [...prev, trimmed]);
    setBarcodeSearch('');
    setShowBarcodeDropdown(false);
  }, [barcodeSearch, barcodeSearchResults, addItemFromSearch]);

  // Process the barcode queue sequentially
  useEffect(() => {
    const processNext = async () => {
      if (isProcessingBarcodeQueue || barcodeQueue.length === 0) return;
      setIsProcessingBarcodeQueue(true);
      setIsSearchingByBarcode(true);
      const nextBarcode = barcodeQueue[0];
      try {
        const response = await itemsAPI.getById(nextBarcode, { include_out_of_stock: true });
        const item = response?.data?.[0];
        if (item && rejectIfNoStock(item)) {
          focusBarcodeInput();
        } else if (item) {
          // Ensure scanned item exists in preloaded items list for dropdown/search
          setItems(prev => {
            const previous = Array.isArray(prev) ? prev : [];
            if (previous.some(i => i && i.ID === item.ID)) return previous;
            return [item, ...previous];
          });
          // If item already exists in details, increment its quantity; otherwise add new row
          const currentDetails = getValues('details') || [];
          const existingIndex = currentDetails.findIndex(d => d && d.SaleItem_ID === item.ID);
          if (existingIndex !== -1) {
            const currentQuantity = parseInt(currentDetails[existingIndex].Quantity) || 0;
            if (canAddOneMore(item, currentQuantity)) {
              setValue(`details.${existingIndex}.Quantity`, String(currentQuantity + 1));
              // Reorder: bring the updated item to the top
              try {
                if (existingIndex > 0) {
                  move(existingIndex, 0);
                }
              } catch (e) {
                // ignore reordering errors
              }
            }
          } else {
            prepend({
              Quantity: '1',
              Price: item.Price || 0,
              SaleItem_ID: item.ID,
              Remarks: ''
            });
          }
          // Immediately return focus to the barcode input
          focusBarcodeInput();
          
        } else {
          addNotification({
            type: 'error',
            title: 'Item Not Found',
            message: `No item found with ID: ${nextBarcode}`
          });
        }
      } catch (error) {
        console.error('Error fetching item by barcode:', error);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to fetch item by barcode'
        });
      } finally {
        // dequeue
        setBarcodeQueue(prev => prev.slice(1));
        setIsSearchingByBarcode(false);
        setIsProcessingBarcodeQueue(false);
      }
    };
    processNext();
  }, [barcodeQueue, isProcessingBarcodeQueue, prepend, move, addNotification, fields.length, rejectIfNoStock, canAddOneMore]);

  // Scoped to `details` so the totals recompute when quantities change rather
  // than on every render of the form.
  const watchedDetails = useWatch({ control, name: 'details' });

  // Helper function to focus barcode input
  const focusBarcodeInput = () => {
    const el = barcodeInputRef.current;
    if (!el) return;
    // Blur any currently focused element to prevent lingering focus (e.g., quantity)
    if (document.activeElement && document.activeElement !== el && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    // Immediate focus/select
    if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    if (typeof el.select === 'function') el.select();
    // Fallback after render cycle
    setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus({ preventScroll: true });
        if (typeof barcodeInputRef.current.select === 'function') {
          barcodeInputRef.current.select();
        }
      }
    }, 0);
    // rAF fallback in case layout shifts steal focus
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus({ preventScroll: true });
          if (typeof barcodeInputRef.current.select === 'function') {
            barcodeInputRef.current.select();
          }
        }
      });
    }
  };

  if (isLoadingPurchaseReturn) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/purchase-returns')}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Purchase Return' : 'New Purchase Return'}
          </h1>
            <p className="text-gray-600">Create a new purchase return to decrement stock</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
        <TotalsBar details={watchedDetails} />
        {/* Purchase Return Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-5 w-5 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Return Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                  {...register('PurchaseReturnDate', { required: 'Return date is required' })}
                  disabled={isEditing}
                  className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {errors.PurchaseReturnDate && (
                <p className="text-red-600 text-sm mt-1">{errors.PurchaseReturnDate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <textarea
                  {...register('Remarks')}
                  rows={1}
                  disabled={isEditing}
                  className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter return remarks..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Return Details */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-red-600" />
              <h2 className="text-lg font-semibold text-gray-900">Return Items</h2>
            </div>
            {!isEditing && (
              <button
                type="button"
                onClick={addDetail}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </button>
            )}
          </div>

          {/* Barcode Scanner Input - Only show in create mode */}
          {!isEditing && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search or Scan Item
              </label>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                  ref={barcodeInputRef}
                type="text"
                  placeholder="Search item name or scan barcode..."
                  value={barcodeSearch}
                  onChange={(e) => setBarcodeSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault(); // Prevent form submission
                      handleBarcodeSearch();
                    } else if (e.key === 'Escape') {
                      setShowBarcodeDropdown(false);
                    }
                  }}
                  onBlur={() => {
                    // Delay so a click on a dropdown row still registers
                    setTimeout(() => setShowBarcodeDropdown(false), 200);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {(isSearchingByBarcode || isProcessingBarcodeQueue || isBarcodeSearching) && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
              {/* Search Results Dropdown */}
              {showBarcodeDropdown && barcodeSearchResults.length > 0 && (
                <div className="relative z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {barcodeSearchResults.map((item, idx) => (
                    <div
                      key={item.ID}
                      onClick={() => addItemFromSearch(item)}
                      className={`p-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${idx === 0 ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-semibold text-gray-600">
                            {item.Name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {item.Name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            ID: {item.ID}
                            {item.Barcode && ` • Barcode: ${item.Barcode}`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-blue-600">
                            {formatPrice(item.Price)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Stock: {item.Stock || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Barcode Queue Progress */}
              {(barcodeQueue.length > 0 || isProcessingBarcodeQueue) && (
                <div className="mt-1.5 text-xs text-gray-600 flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                  <span>Processing barcode{isProcessingBarcodeQueue ? '...' : ''}</span>
                  {barcodeQueue.length > 0 && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded-full border text-gray-700">{barcodeQueue.length} pending</span>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Type to search items or scan barcode. Press Enter to add first result.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {fields.map((field, index) => (
              <div key={field.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2.5">
              
          </div>

                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item *
                    </label>
                    <div className="relative dropdown-container">
                      <div
                        onClick={() => !isEditing && toggleDropdown(index)}
                        onKeyDown={(e) => {
                          if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            toggleDropdown(index);
                          }
                        }}
                        tabIndex={isEditing ? -1 : 0}
                        role="button"
                        aria-expanded={dropdownStates[index]}
                        aria-haspopup="listbox"
                        className={`w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between transition-colors ${
                          isEditing 
                            ? 'bg-gray-100 cursor-not-allowed' 
                            : 'cursor-pointer bg-white hover:border-gray-400'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          {getSelectedItem(index) ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-semibold text-red-600">
                                  {getSelectedItem(index).Name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {getSelectedItem(index).Name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatPrice(getSelectedItem(index).Price)}
                                  {getSelectedItem(index).Barcode && ` • Barcode: ${getSelectedItem(index).Barcode}`}
                                </div>
                              </div>
              </div>
            ) : (
                            <span className="text-gray-500">Select an item</span>
                          )}
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${dropdownStates[index] ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {!isEditing && dropdownStates[index] && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                          <div className="p-1.5 border-b border-gray-200">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search items..."
                                value={searchTerms[index] || ''}
                                onChange={(e) => setSearchTerm(index, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault(); // Prevent form submission
                                  } else if (e.key === 'Escape') {
                                    setDropdownStates(prev => ({ ...prev, [index]: false }));
                                  }
                                }}
                                className="w-full pl-10 pr-10 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                autoFocus
                              />
                              {isSearching[index] && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {isSearching[index] ? (
                              <div className="p-3 text-center">
                                <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  <span>Searching items...</span>
                      </div>
                    </div>
                            ) : getFilteredItems(index).length > 0 ? (
                              getFilteredItems(index).map((item) => (
                                <div
                                  key={item.ID}
                                  onClick={() => selectItem(index, item)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      selectItem(index, item);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="option"
                                  className="p-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 focus:bg-gray-50 focus:outline-none"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                      <span className="text-sm font-semibold text-gray-600">
                                        {item.Name?.charAt(0)?.toUpperCase() || '?'}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 truncate">
                                        {item.Name}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">
                                        ID: {item.ID}
                                        {item.Barcode && ` • Barcode: ${item.Barcode}`}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-sm font-semibold text-red-600">
                                        {formatPrice(item.Price)}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Stock: {item.Stock || 0}
                                      </div>
                                    </div>
                                  </div>
                  </div>
                              ))
                            ) : (
                              <div className="p-3 text-center text-gray-500 text-sm">
                                {searchTerms[index] ? 'No items found matching your search' : 'No items available'}
              </div>
            )}
          </div>
        </div>
                      )}
                    </div>
                    {errors.details?.[index]?.SaleItem_ID && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.details[index].SaleItem_ID.message}
                      </p>
                    )}
                  </div>
                  
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity *
                      </label>
                      <input
                      ref={(el) => {
                        quantityInputRefs.current[`quantity-${index + 1}`] = el;
                      }}
                        type="number"
                      step="1"
                        min="1"
                      placeholder="Enter quantity"
                      disabled={isEditing}
                      {...register(`details.${index}.Quantity`, {
                        required: 'Quantity is required',
                        min: { value: 1, message: 'Quantity must be at least 1' },
                        validate: (value, formValues) => {
                          const itemId = Number(formValues.details?.[index]?.SaleItem_ID);
                          const item = items.find(i => i && i.ID === itemId);
                          if (!item) return true;
                          const stock = availableStock(item);
                          return (parseInt(value) || 0) <= stock || `Only ${stock} in stock`;
                        }
                      })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // Prevent form submission
                          
                          // Get the current quantity value
                          const currentQuantity = parseInt(e.target.value) || 0;
                          
                          // Validate quantity before focusing barcode input
                          if (currentQuantity >= 1) {
                            focusBarcodeInput();
                          } else {
                            addNotification({
                              type: 'error',
                              title: 'Invalid Quantity',
                              message: 'Please enter a quantity of at least 1'
                            });
                            // Keep focus on quantity input for correction
                            e.target.focus();
                            e.target.select();
                          }
                        }
                      }}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {errors.details?.[index]?.Quantity && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.details[index].Quantity.message}
                      </p>
                    )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Item price"
                        disabled={true}
                        {...register(`details.${index}.Price`)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                      />
                    </div>


                  <div className="flex items-center justify-center">
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => removeDetail(index)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors flex items-center justify-center"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        {/* Actions */}
        {isEditing ? (
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => navigate('/purchase-returns')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Back to Purchase Returns
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/purchase-returns')}
              className="px-3.5 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
              {isSubmitting ? 'Creating...' : 'Create Purchase Return'}
          </button>
        </div>
        )}
      </form>
    </div>
  );
};

export default PurchaseReturnForm;